import { debounce } from 'lodash-es'
import { useSearchParams } from 'next/navigation'
import { useEffect, useTransition } from 'react'

import {
  defaultFilters,
  useSearchFilterStore,
  ZSearchFilter,
} from '@/entities/map'
import { RoomSortOption } from '@/entities/room'
import { convertToFilterSearchParams } from '@/features/search-map/lib/search-params'
import { useRouter } from '@/shared/i18n'

const PATHNAME = '/guest/room'

export const useKeywordSearchParams = () => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isPageReloading, startTransition] = useTransition()

  const filterActions = useSearchFilterStore(state => state.actions)

  const updateSearchParams = () => {
    const roomFilters = useSearchFilterStore.getState().filters
    const { keyword, sort } = useSearchFilterStore.getState()

    const filterSearchParams = convertToFilterSearchParams({
      keyword,
      sort,
      ...roomFilters,
    })

    window.scrollTo({ top: 0, behavior: 'instant' })
    startTransition(() => {
      router.replace(
        `${PATHNAME}${filterSearchParams && `?${filterSearchParams}`}`,
      )
    })
  }

  useEffect(() => {
    const keyword = searchParams?.get('keyword') || ''
    const sort =
      (searchParams?.get('sort') as RoomSortOption) || RoomSortOption.POPULAR

    filterActions.update({
      ...ZSearchFilter.parse({
        ...defaultFilters,
        ...Object.fromEntries(searchParams?.entries() ?? []),
      }),
    })
    filterActions.updateKeyword(keyword)
    filterActions.updateSort(sort)

    const unsubscribeKeyword = useSearchFilterStore.subscribe(
      state => state.keyword,
      updateSearchParams,
    )

    const unsubscribeFilter = useSearchFilterStore.subscribe(
      state => state.filters,
      debounce(updateSearchParams, 300),
    )

    const unsubscribeSort = useSearchFilterStore.subscribe(
      state => state.sort,
      updateSearchParams,
    )

    return () => {
      unsubscribeKeyword()
      unsubscribeFilter()
      unsubscribeSort()
    }
  }, [])

  return { isPageReloading }
}
